import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, User, ShoppingBag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

const LINKS = [
    { to: "/materials", label: "Research Materials" },
    { to: "/standards", label: "Our Standards" },
    { to: "/testing", label: "Testing & COAs" },
    { to: "/about", label: "About" },
    { to: "/faq", label: "FAQ" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const { user } = useAuth();
    const { count } = useCart();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    return (
        <>
            <header className="fixed top-0 inset-x-0 z-50">
                {/* Slate announcement bar */}
                <div className="bg-brand h-9 flex items-center">
                    <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full flex items-center justify-between">
                        <p className="hidden sm:block text-[0.55rem] tracking-[0.3em] uppercase text-gold/90">
                            Research · Quality · Integrity
                        </p>
                        <p className="text-[0.55rem] tracking-[0.3em] uppercase text-gold/90 sm:hidden">
                            Grayhaven Research
                        </p>
                        <p className="inline-flex items-center gap-2 text-[0.55rem] tracking-[0.3em] uppercase text-slate-300">
                            <span className="h-1 w-1 rounded-full bg-gold" aria-hidden="true" />
                            Research Use Only
                        </p>
                    </div>
                </div>
            <nav
                data-testid="main-nav"
                className={`transition-colors duration-500 ${
                    scrolled ? "bg-ink/90 backdrop-blur-md border-b border-stone-200" : "bg-ink/60 backdrop-blur-sm border-b border-transparent"
                }`}
            >
                <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo-link">
                        <span className="h-11 w-11 rounded-full overflow-hidden ring-1 ring-gold/30 group-hover:ring-gold/60 transition-shadow duration-300 shrink-0 bg-ink">
                            <img
                                src="/assets/grayhaven-mark-dna.png"
                                alt="Grayhaven Research monogram"
                                className="h-full w-full object-cover"
                            />
                        </span>
                        <span className="leading-none">
                            <span className="block font-serif text-lg tracking-[0.18em] text-brand">GRAYHAVEN</span>
                            <span className="block text-[0.55rem] tracking-[0.5em] text-gold mt-1">RESEARCH</span>
                        </span>
                    </Link>

                    <div className="hidden lg:flex items-center gap-6">
                        {LINKS.map((link) => (
                            <NavLink
                                key={link.to}
                                to={link.to}
                                data-testid={`nav-link-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                className={({ isActive }) =>
                                    `nav-link text-[0.65rem] uppercase tracking-[0.18em] font-medium ${
                                        isActive ? "active text-wine" : "text-slate-500 hover:text-brand"
                                    }`
                                }
                            >
                                {link.label}
                            </NavLink>
                        ))}
                        {user && (
                            <NavLink
                                to="/account"
                                data-testid="nav-link-my-account"
                                className={({ isActive }) =>
                                    `nav-link inline-flex items-center gap-1.5 text-[0.65rem] uppercase tracking-[0.18em] font-semibold ${
                                        isActive ? "active text-wine" : "text-brand hover:text-wine"
                                    }`
                                }
                            >
                                <User className="h-3.5 w-3.5" /> My Account
                            </NavLink>
                        )}
                        <NavLink
                            to="/cart"
                            data-testid="nav-cart-link"
                            aria-label="Cart"
                            className={({ isActive }) =>
                                `relative inline-flex items-center ${isActive ? "text-wine" : "text-brand hover:text-wine"} transition-colors duration-300`
                            }
                        >
                            <ShoppingBag className="h-[18px] w-[18px]" strokeWidth={1.5} />
                            {count > 0 && (
                                <span
                                    data-testid="nav-cart-count"
                                    className="absolute -top-2 -right-2.5 h-4 min-w-4 px-1 rounded-full bg-wine text-white text-[0.55rem] font-semibold flex items-center justify-center"
                                >
                                    {count}
                                </span>
                            )}
                        </NavLink>
                    </div>

                    <div className="lg:hidden flex items-center gap-5">
                        <NavLink
                            to="/cart"
                            data-testid="mobile-nav-cart-link"
                            aria-label="Cart"
                            className="relative inline-flex items-center text-brand hover:text-wine transition-colors duration-300"
                        >
                            <ShoppingBag className="h-[18px] w-[18px]" strokeWidth={1.5} />
                            {count > 0 && (
                                <span className="absolute -top-2 -right-2.5 h-4 min-w-4 px-1 rounded-full bg-wine text-white text-[0.55rem] font-semibold flex items-center justify-center">
                                    {count}
                                </span>
                            )}
                        </NavLink>
                        <button
                            type="button"
                            aria-label="Open menu"
                            data-testid="mobile-menu-open-btn"
                            onClick={() => setOpen(true)}
                            className="text-brand hover:text-wine transition-colors duration-300"
                        >
                            <Menu className="h-6 w-6" />
                        </button>
                    </div>
                </div>
            </nav>
            </header>

            <div
                data-testid="mobile-menu"
                className={`fixed inset-0 z-[70] bg-ink flex flex-col transition-opacity duration-300 lg:hidden ${
                    open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
                }`}
            >
                <div className="h-20 px-6 flex items-center justify-between border-b border-stone-200">
                    <span className="font-serif text-lg tracking-[0.18em] text-brand">GRAYHAVEN</span>
                    <button
                        type="button"
                        aria-label="Close menu"
                        data-testid="mobile-menu-close-btn"
                        onClick={() => setOpen(false)}
                        className="text-brand hover:text-wine transition-colors duration-300"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>
                <div className="flex-1 flex flex-col justify-center px-8 gap-2 overflow-y-auto">
                    {user && (
                        <NavLink
                            to="/account"
                            data-testid="mobile-nav-link-my-account"
                            className={({ isActive }) =>
                                `font-serif text-3xl py-3 border-b border-stone-200 transition-colors duration-300 ${
                                    isActive ? "text-wine" : "text-wine/80 hover:text-wine"
                                }`
                            }
                        >
                            My Account
                        </NavLink>
                    )}
                    {LINKS.map((link, i) => (
                        <NavLink
                            key={link.to}
                            to={link.to}
                            data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            className={({ isActive }) =>
                                `font-serif text-3xl py-3 border-b border-stone-200 transition-colors duration-300 ${
                                    link.to === "/materials" ? "text-wine" : isActive ? "text-wine" : "text-brand hover:text-wine"
                                }`
                            }
                            style={{ transitionDelay: `${i * 20}ms` }}
                        >
                            {link.label}
                        </NavLink>
                    ))}
                </div>
                <p className="px-8 pb-10 text-xs tracking-[0.2em] uppercase text-slate-400">
                    Quality. Transparency. Integrity.
                </p>
            </div>
        </>
    );
}
